import React, { useCallback, useState } from "react";
import { useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  ConciergeWidget,
  ConciergeFloatingButton,
  ConciergeWidgetState,
  usePageContext,
} from "@brandclub/common-ui";
import { useConciergeWidget } from "@/utils/hooks/useConciergeWidget";
import { useIsConciergePage } from "@/utils/hooks/useIsConciergePage";
import { useIsConciergeEnabled } from "@brandclub/common-ui";
import { useAppSelector } from "@/redux/hooks";
// Import Tailwind CSS for concierge components
import "./concierge-tailwind.css";

// Create a query client for the widget
const queryClient = new QueryClient();

// Shared state for suggested pill queries between FloatingButton and Widget adapters
// Using a simple module-level state with subscribers pattern (avoids Zustand cross-bundle issues)
// This is also used by BrandStoreLandingPage for conversation starter clicks
interface PillQueryState {
  query: string | null;
  stacklineSku: string | null;
}
type PillQueryListener = (state: PillQueryState) => void;
let pillQueryListeners: PillQueryListener[] = [];
let currentPillQueryState: PillQueryState = { query: null, stacklineSku: null };

/**
 * Sets a pending query that will be auto-sent when the concierge widget opens.
 * This function is exported so it can be called from other components (e.g., BrandStoreLandingPage)
 * to trigger auto-send behavior for conversation starters, suggested pills, etc.
 * 
 * @param query - The query text to send
 * @param stacklineSku - Optional product SKU for product context
 */
export const setConciergeQuery = (query: string | null, stacklineSku: string | null = null) => {
  currentPillQueryState = { query, stacklineSku };
  pillQueryListeners.forEach((listener) => listener(currentPillQueryState));
};

const usePillQuery = () => {
  const [pillQueryState, _setPillQueryState] = useState<PillQueryState>(currentPillQueryState);

  React.useEffect(() => {
    const listener: PillQueryListener = (state) => {
      _setPillQueryState(state);
    };
    pillQueryListeners.push(listener);
    return () => {
      pillQueryListeners = pillQueryListeners.filter((l) => l !== listener);
    };
  }, []);

  return {
    pillQuery: pillQueryState.query,
    pillStacklineSku: pillQueryState.stacklineSku,
    setConciergeQuery,
    clearPillQuery: () => setConciergeQuery(null, null),
  };
};

/**
 * Adapter that wraps Redux-based widget state to match ConciergeWidgetState interface
 */
const useWidgetStateAdapter = (): ConciergeWidgetState & {
  isMaximized: boolean;
  setMaximized: (v: boolean) => void;
} => {
  const { isOpen, isMaximized, toggle, closeWidget, openWidget, setMaximized } =
    useConciergeWidget();

  return {
    isOpen,
    isMaximized,
    toggle,
    close: closeWidget,
    open: openWidget,
    setMaximized,
  };
};

export const ConciergeWidgetAdapter: React.FC = React.memo(() => {
  const widgetState = useWidgetStateAdapter();
  const isConciergePage = useIsConciergePage();
  const pageContext = usePageContext();
  const userProfile = useAppSelector(({ userProfile }) => userProfile);
  const { showWidget } = useIsConciergeEnabled({
    email: userProfile?.email,
  });
  const { pillQuery, pillStacklineSku, clearPillQuery } = usePillQuery();

  const handleMaximize = useCallback(() => {
    widgetState.setMaximized(true);
  }, [widgetState]);

  const handleMinimize = useCallback(() => {
    widgetState.setMaximized(false);
  }, [widgetState]);

  const handleInitialQueryProcessed = useCallback(() => {
    clearPillQuery();
  }, [clearPillQuery]);

  // Hide widget when on the concierge page or when not in concierge split
  if (isConciergePage || !showWidget) {
    return null;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ConciergeWidget
        widgetState={widgetState}
        pageContext={pageContext}
        showMaximize={true}
        isMaximized={widgetState.isMaximized}
        onMaximize={handleMaximize}
        onMinimize={handleMinimize}
        isConciergeEnabled={showWidget}
        initialQuery={pillQuery || undefined}
        initialQueryStacklineSku={pillStacklineSku || undefined}
        onInitialQueryProcessed={handleInitialQueryProcessed}
      />
    </QueryClientProvider>
  );
});

export const ConciergeFloatingButtonAdapter: React.FC = React.memo(() => {
  const widgetState = useWidgetStateAdapter();
  const location = useLocation();
  const isConciergePage = useIsConciergePage();
  const userProfile = useAppSelector(({ userProfile }) => userProfile);
  const { showWidget } = useIsConciergeEnabled({
    email: userProfile?.email,
  });
  const { setConciergeQuery: setPillQuery } = usePillQuery();

  const handleSuggestedPillClick = useCallback(
    (message: string, stacklineSku?: string) => {
      setPillQuery(message, stacklineSku || null);
    },
    [setPillQuery]
  );

  return (
    <ConciergeFloatingButton
      widgetState={widgetState}
      isConciergeEnabled={showWidget}
      hideOnConciergePage={isConciergePage}
      isMaximized={widgetState.isMaximized}
      onSuggestedPillClick={handleSuggestedPillClick}
      currentPathname={location.pathname}
    />
  );
});
